import React, { useState } from "react";
import "./components.css";
import { RadioGroup, CheckboxGroup, DateRangePicker, NumberRange } from "./FilteringUtils.jsx";

const FilteringSidebar = ({ onCategoryChange }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const [filters, setFilters] = useState({
    operationType: [],
    ntcSuitability: "",
    ntcAttendance: "",
    theatreDateRange: { start: "", end: "" },
    roboticSurgery: "",
    preassessmentPassed: "",
    simd: "",
    surgerySide: "",
    priorityScoreRange: { min: "", max: "" },
    bedRequired: ""
  });

  const toggleSidebar = () => setIsCollapsed(prev => !prev);

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setFilters(prev => ({
      ...prev,
      operationType: checked
        ? [...prev.operationType, value]
        : prev.operationType.filter(item => item !== value)
    }));
  };

  const handleRadioToggle = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: prev[key] === value ? "" : value
    }));
  };

  const handleInputChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleNestedChange = (group, key, value) => {
    setFilters(prev => ({
      ...prev,
      [group]: {
        ...prev[group],
        [key]: value
      }
    }));
  };

  const applyFilters = () => {
    const {
      operationType,
      ntcSuitability,
      ntcAttendance,
      theatreDateRange,
      roboticSurgery,
      preassessmentPassed,
      simd,
      surgerySide,
      priorityScoreRange,
      bedRequired
    } = filters;

  onCategoryChange({
      operationType: operationType.length ? operationType.join(",") : undefined,
      ntcSuitability: parseBoolean(ntcSuitability),
      ntcAttendance: parseBoolean(ntcAttendance),
      roboticSurgery: parseBoolean(roboticSurgery),
      preassessmentPassed: parseBoolean(preassessmentPassed),
      surgerySide: surgerySide === "left" ? "L" : surgerySide === "right" ? "R" : undefined,
      simd: simd ? parseInt(simd, 10) : undefined,
      bedRequired: parseBoolean(bedRequired),
      theatreDateRangeStart: theatreDateRange.start || undefined,
      theatreDateRangeEnd: theatreDateRange.end || undefined,
      priorityScoreRangeMin: priorityScoreRange.min || undefined,
      priorityScoreRangeMax: priorityScoreRange.max || undefined
    });
  };

  const clearFilters = () => {
    const initialFilters = {
      operationType: [],
      ntcSuitability: "",
      ntcAttendance: "",
      theatreDateRange: { start: "", end: "" },
      roboticSurgery: "",
      preassessmentPassed: "",
      simd: "",
      surgerySide: "",
      priorityScoreRange: { min: "", max: "" },
      bedRequired: ""
    };
    setFilters(initialFilters);
    onCategoryChange({});
  };
  

  return (
    <div className={`sidebar ${isCollapsed ? "collapsed" : ""}`}>
      <div className="sidebar-header">
        <h3 className="sidebar-title">{isCollapsed ? "" : "Advanced Filtering"}</h3>
        <button className="toggle-sidebar-button" onClick={toggleSidebar}>
          <span className="arrow">{isCollapsed ? "◀" : "▶"}</span>
        </button>
      </div>

      {!isCollapsed && (
        <div className="sidebar-content">
          <h4> Scottish Index of Multiple Deprivation (Decile)</h4>
          <input
            type="number"
            min="1"
            max="10"
            placeholder="Enter SIMD..."
            value={filters.simd}
            onChange={(e) => handleInputChange("simd", e.target.value)}
          />

          <CheckboxGroup
            label="Operation Type"
            options={[
              { id: "hip", label: "Hip Replacement", value: "0" },
              { id: "knee", label: "Knee Replacement", value: "1" }
            ]}
            selected={filters.operationType}
            onChange={handleCheckboxChange}
          />

          <RadioGroup
            label="Surgery Side"
            name="surgerySide"
            options={[
              { id: "left", label: "Left", value: "left" },
              { id: "right", label: "Right", value: "right" }
            ]}
            selected={filters.surgerySide}
            onToggle={(value) => handleRadioToggle("surgerySide", value)}
          />

          <RadioGroup
            label="NTC Suitability"
            name="ntcSuitability"
            options={[
              { id: "ouiNtccc", label: "Yes", value: "yes" },
              { id: "swipernontc", label: "No", value: "no" }
            ]}
            selected={filters.ntcSuitability}
            onToggle={(value) => handleRadioToggle("ntcSuitability", value)}
          />

          <RadioGroup
            label="NTC Willingness"
            name="ntcAttendance"
            options={[
              { id: "attendyes", label: "Yes", value: "yes" },
              { id: "attendno", label: "No", value: "no" }
            ]}
            selected={filters.ntcAttendance}
            onToggle={(value) => handleRadioToggle("ntcAttendance", value)}
          />

          <RadioGroup
            label="HDU/ITU Bed"
            name="bedRequired"
            options={[
              { id: "ouiouibed", label: "Yes", value: "yes" },
              { id: "nonobed", label: "No", value: "no" }
            ]}
            selected={filters.bedRequired}
            onToggle={(value) => handleRadioToggle("bedRequired", value)}
          />

          <RadioGroup
            label="Robotic Surgery"
            name="roboticSurgery"
            options={[
              { id: "yeeeerobots", label: "Yes", value: "yes" },
              { id: "badrobots", label: "No", value: "no" }
            ]}
            selected={filters.roboticSurgery}
            onToggle={(value) => handleRadioToggle("roboticSurgery", value)}
          />

          <RadioGroup
            label="Preassessment Status"
            name="preassessmentPassed"
            options={[
              { id: "passpasspass", label: "Passed", value: "yes" },
              { id: "failure!:", label: "Failed", value: "no" }
            ]}
            selected={filters.preassessmentPassed}
            onToggle={(value) => handleRadioToggle("preassessmentPassed", value)}
          />

          <DateRangePicker
            label="Theatre Date"
            value={filters.theatreDateRange}
            onChange={(key, value) => handleNestedChange("theatreDateRange", key, value)}
          />

          <NumberRange
            label="Priority Score (3 dp)"
            value={filters.priorityScoreRange}
            onChange={(key, value) => handleNestedChange("priorityScoreRange", key, value)}
          />

          <div className="filter-actions">

            <button className="filter-button apply-button" onClick={applyFilters}>
                  Apply Filters
            </button>
            <button className="filter-button clear-button" onClick={clearFilters}>
                  Clear Filters
            </button>
          </div>

        </div>
      )}
    </div>
  );
};

const parseBoolean = (val) => (val === "yes" ? true : val === "no" ? false : undefined);

export default FilteringSidebar;
